import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Spinner, Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSyncAlt, faSearch,
  faTimes, faSave, faExclamationTriangle, faCheckCircle,
  faFilter, faEraser, faCalendarAlt, faTag, faInfoCircle,
  faChevronRight,
  faAnglesRight,
  faAnglesLeft,
  faChevronLeft
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

// ============================================================
// CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('ParametresPaieCacheDB');
db.version(1).stores({
  parametres: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key) {
  try {
    const entry = await db.parametres.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data) {
  await db.parametres.put({ key, data, timestamp: Date.now() });
}
async function clearCache() {
  await db.parametres.clear();
}

const API_URL = `${API_CONFIG2.apiUrl}paie/api_parametres_paie.php`;

const ManageParametresPaie = () => {
  // --- États ---
  const [parametres, setParametres] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnnee, setFilterAnnee] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: '', valeur: '', description: '', annee: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [toast, setToast] = useState(null);
  const abortRef = useRef(null);
  const isMounted = useRef(true);

  // --- Toast ---
  const showToast = (msg, variant = 'success') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 5000);
  };

  // --- Fetch data (avec cache) ---
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const cacheKey = 'parametres_list';

    try {
      if (!forceRefresh) {
        const cached = await getCached(cacheKey);
        if (cached) {
          setParametres(cached);
          setDataSource('cache');
          setLoading(false);
          return;
        }
      }

      const url = `${API_URL}?action=list&unique=${Date.now()}`;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.status === 'success') {
        setParametres(data.data);
        setDataSource('live');
        await setCached(cacheKey, data.data);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      const cached = await getCached(cacheKey);
      if (cached) {
        setParametres(cached);
        setDataSource('cache (expiré)');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  // --- Filtrage et recherche ---
  useEffect(() => {
    let result = [...parametres];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(p =>
        p.nom?.toLowerCase().includes(term) ||
        p.valeur?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term)
      );
    }
    if (filterAnnee) {
      result = result.filter(p => p.annee === parseInt(filterAnnee));
    }
    // Trier par nom
    result.sort((a, b) => a.nom.localeCompare(b.nom));
    setFiltered(result);
    setCurrentPage(1);
  }, [parametres, searchTerm, filterAnnee]);

  // --- Pagination ---
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  // --- Initialisation ---
  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => {
      isMounted.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // --- CRUD ---
  const openCreateModal = () => {
    setEditing(null);
    setForm({ nom: '', valeur: '', description: '', annee: '' });
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setForm({
      nom: item.nom,
      valeur: item.valeur,
      description: item.description || '',
      annee: item.annee || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.nom.trim()) errors.nom = 'Le nom est requis';
    if (!form.valeur.trim()) errors.valeur = 'La valeur est requise';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    const payload = {
      nom: form.nom.trim(),
      valeur: form.valeur.trim(),
      description: form.description.trim() || null,
      annee: form.annee ? parseInt(form.annee) : null
    };
    try {
      let url, options;
      if (editing) {
        payload.id = editing.id;
        url = `${API_URL}?action=update`;
        options = { method: 'PUT', body: JSON.stringify(payload) };
      } else {
        url = `${API_URL}?action=create`;
        options = { method: 'POST', body: JSON.stringify(payload) };
      }
      const response = await fetch(url, {
        ...options,
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message || 'Erreur');
      showToast(editing ? 'Paramètre mis à jour ✓' : 'Paramètre créé ✓', 'success');
      setShowModal(false);
      // Invalider le cache et recharger
      await clearCache();
      fetchData(true);
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}?action=delete&id=${deleteTarget.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message || 'Erreur');
      showToast('Paramètre supprimé ✓', 'success');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await clearCache();
      fetchData(true);
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleClearCache = async () => {
    await clearCache();
    showToast('Cache vidé', 'success');
    fetchData(true);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterAnnee('');
  };

  // --- Rendu squelette de chargement ---
  const renderSkeleton = () => (
    <div className="table-responsive">
      <table className="glass-table">
        <thead><tr><th>Nom</th><th>Valeur</th><th>Année</th><th>Actions</th></tr></thead>
        <tbody>
          {[...Array(5)].map((_, i) => (
            <tr key={i}>
              <td><div className="skeleton-text"></div></td>
              <td><div className="skeleton-text"></div></td>
              <td><div className="skeleton-text"></div></td>
              <td><div className="skeleton-actions"><div className="skeleton-btn"></div><div className="skeleton-btn"></div></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --bg-dark: #0a0f1f;
          --bg-card: rgba(20, 40, 80, 0.55);
          --text-light: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0,0,0,0.4);
        }
        .parametres-wrapper {
          background: radial-gradient(ellipse at 20% 30%, #132a4a, var(--bg-dark));
          padding: 1.5rem;
          border-radius: 24px;
          min-height: 70vh;
          margin-top: 0.5rem;
        }
        .glass-card {
          background: var(--bg-card);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(59,130,246,0.12);
          border-radius: var(--radius);
          padding: 1.2rem 1.5rem;
          box-shadow: var(--shadow);
        }
        .badge-source {
          font-size: 0.65rem;
          padding: 0.2rem 0.6rem;
          border-radius: 30px;
          background: rgba(59,130,246,0.12);
          color: #4f8cff;
          border: 1px solid rgba(59,130,246,0.08);
          margin-left: 0.5rem;
          font-weight: 400;
        }
        .badge-source.live { background: rgba(46,204,113,0.12); color: #2ecc71; border-color: rgba(46,204,113,0.08); }
        .badge-source.warning { background: rgba(245,158,11,0.12); color: #f59e0b; border-color: rgba(245,158,11,0.08); }

        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.8rem;
          margin-bottom: 1.2rem;
        }
        .header-section h5 {
          font-weight: 700;
          color: var(--text-light);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.2rem;
        }

        .btn-glass {
          padding: 8px 18px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-light);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.85rem;
        }
        .btn-glass:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
        }
        .btn-glass.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(59,130,246,0.25);
        }
        .btn-glass.primary:hover:not(:disabled) {
          background: #1d4ed8;
          box-shadow: 0 8px 28px rgba(59,130,246,0.35);
        }
        .btn-glass.danger {
          background: rgba(239,68,68,0.12);
          color: #f87171;
          border: 1px solid rgba(239,68,68,0.2);
        }
        .btn-glass.danger:hover:not(:disabled) { background: rgba(239,68,68,0.25); }
        .btn-glass.outline { background: transparent; border: 1px solid rgba(255,255,255,0.08); }
        .btn-glass.outline:hover { background: rgba(255,255,255,0.04); }
        .btn-glass.sm { padding: 4px 12px; font-size: 0.75rem; }

        .filters-bar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.6rem;
          margin-bottom: 1.2rem;
        }
        .filters-bar .search-wrapper {
          flex: 1;
          min-width: 180px;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          padding: 0.2rem 0.6rem 0.2rem 1rem;
          transition: 0.3s;
        }
        .filters-bar .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.08);
        }
        .filters-bar .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-light);
          outline: none;
          flex: 1;
          padding: 0.5rem 0;
          font-size: 0.9rem;
        }
        .filters-bar .search-wrapper input::placeholder { color: var(--text-muted); }
        .filters-bar .filter-select {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          color: var(--text-light);
          padding: 0.35rem 0.8rem;
          outline: none;
          font-size: 0.85rem;
          min-width: 100px;
          cursor: pointer;
        }
        .filters-bar .filter-select option { background: #0b1428; }
        .filters-bar .reset-btn {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.2rem 0.6rem;
          border-radius: 30px;
          transition: 0.2s;
          font-size: 0.8rem;
        }
        .filters-bar .reset-btn:hover { background: rgba(255,255,255,0.04); color: var(--text-light); }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 12px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tbody tr { transition: background 0.15s; }
        .glass-table tbody tr:hover { background: rgba(255,255,255,0.03); }

        .skeleton-text {
          height: 14px;
          background: rgba(255,255,255,0.06);
          border-radius: 4px;
          width: 80%;
          animation: pulse 1.5s ease-in-out infinite;
        }
        .skeleton-actions { display: flex; gap: 6px; }
        .skeleton-btn {
          width: 30px;
          height: 30px;
          background: rgba(255,255,255,0.06);
          border-radius: 30px;
          animation: pulse 1.5s ease-in-out infinite;
        }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

        .pagination-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.8rem;
          margin-top: 1.2rem;
          padding-top: 0.8rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-bar .info { color: var(--text-secondary); font-size: 0.85rem; }
        .pagination-controls {
          display: flex;
          gap: 3px;
          align-items: center;
          flex-wrap: wrap;
        }
        .pagination-controls button {
          padding: 4px 10px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-light);
          transition: 0.2s;
          font-size: 0.8rem;
          cursor: pointer;
          min-width: 30px;
        }
        .pagination-controls button:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
        .pagination-controls button:disabled { opacity: 0.3; cursor: not-allowed; }
        .pagination-controls button.active { background: var(--accent-blue); color: #fff; border-color: var(--accent-blue); }
        .pagination-controls .ellipsis { color: var(--text-muted); padding: 0 3px; }
        .pagination-controls .per-page { display: flex; align-items: center; gap: 4px; margin-left: 0.3rem; }
        .pagination-controls .per-page select { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 30px; color: var(--text-light); padding: 0.1rem 0.4rem; outline: none; font-size: 0.7rem; cursor: pointer; }

        .modal-glass .modal-content {
          background: rgba(10,20,45,0.92);
          backdrop-filter: blur(30px);
          border: 1px solid rgba(59,130,246,0.12);
          border-radius: 28px;
          color: var(--text-light);
        }
        .modal-glass .modal-header { border-bottom: 1px solid rgba(59,130,246,0.06); padding: 1rem 1.5rem; }
        .modal-glass .modal-footer { border-top: 1px solid rgba(59,130,246,0.06); padding: 0.8rem 1.5rem; flex-wrap: wrap; }
        .modal-glass .modal-body { padding: 1.5rem; }
        .modal-glass .form-control, .modal-glass .form-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-light);
          border-radius: 12px;
        }
        .modal-glass .form-control:focus, .modal-glass .form-select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .modal-glass .form-control.is-invalid { border-color: #f87171; }
        .modal-glass .invalid-feedback { color: #f87171; font-size: 0.8rem; }

        .toast-container {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 9999;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .toast-item {
          background: rgba(10,20,45,0.9);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59,130,246,0.2);
          border-radius: 16px;
          padding: 12px 16px;
          color: var(--text-light);
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          animation: slideIn 0.4s ease;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn { from { opacity:0; transform:translateX(60px); } to { opacity:1; transform:translateX(0); } }
        .btn-close-custom { background:transparent; border:none; color:var(--text-secondary); cursor:pointer; font-size:1.2rem; padding:0 4px; margin-left:auto; }
        .btn-close-custom:hover { color:var(--text-light); }

        .empty-state { text-align:center; padding:2rem 0; color:var(--text-secondary); }
        .empty-state .icon { font-size:2.5rem; color:#5e6f94; opacity:0.3; margin-bottom:0.5rem; }
        .alert-glass { background:rgba(255,255,255,0.03); backdrop-filter:blur(8px); border:1px solid rgba(255,255,255,0.06); border-radius:14px; color:var(--text-light); padding:10px 16px; }
        .alert-glass-danger { border-color:rgba(239,68,68,0.2); background:rgba(239,68,68,0.04); color:#f87171; }

        @media (max-width: 768px) {
          .filters-bar { flex-direction: column; align-items: stretch; }
          .filters-bar .search-wrapper { width:100%; min-width:unset; }
          .header-section { flex-direction: column; align-items: stretch; }
          .header-section .actions { justify-content: flex-start; }
          .pagination-bar { flex-direction: column; align-items: center; }
        }
        @media (max-width: 480px) {
          .parametres-wrapper { padding: 0.8rem; }
          .glass-card { padding: 0.8rem; }
        }
      `}</style>

      <div className="parametres-wrapper">
        {/* Header */}
        <div className="header-section">
          <h5>
            <FontAwesomeIcon icon={faTag} className="text-primary me-2" />
            Paramètres de paie
            <span className="badge-source">{filtered.length}</span>
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (expiré)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (expiré)' ? '⚠️ Expiré' :
               dataSource === 'live' ? '🔄 Live' : ''}
            </span>
          </h5>
          <div className="d-flex gap-2 flex-wrap actions">
            <button className="btn-glass outline sm" onClick={() => fetchData(true)} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button className="btn-glass outline sm" onClick={handleClearCache} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faEraser} /> Vider cache
            </button>
            <button className="btn-glass primary sm" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="filters-bar">
          <div className="search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="text-muted" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-btn" onClick={() => setSearchTerm('')}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <Form.Control
            type="number"
            className="filter-select"
            placeholder="Année"
            value={filterAnnee}
            onChange={(e) => setFilterAnnee(e.target.value)}
            style={{ minWidth: '80px' }}
          />
          {(searchTerm || filterAnnee) && (
            <button className="reset-btn" onClick={resetFilters}>
              <FontAwesomeIcon icon={faTimes} className="me-1" /> Réinitialiser
            </button>
          )}
        </div>

        {/* Erreur */}
        {error && (
          <div className="alert-glass alert-glass-danger mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </div>
        )}

        {/* Tableau */}
        {loading && !parametres.length ? renderSkeleton() : (
          <>
            <div className="glass-card p-0 overflow-hidden">
              <div className="table-responsive">
                <table className="glass-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Valeur</th>
                      <th>Description</th>
                      <th>Année</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.length === 0 ? (
                      <tr><td colSpan="5" className="text-center py-4 text-secondary">Aucun paramètre trouvé.</td></tr>
                    ) : (
                      paginatedItems.map(item => (
                        <tr key={item.id}>
                          <td><strong>{item.nom}</strong></td>
                          <td>{item.valeur}</td>
                          <td>{item.description || '—'}</td>
                          <td>{item.annee || '—'}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <button className="btn-glass icon-only sm" onClick={() => openEditModal(item)} title="Modifier">
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button className="btn-glass icon-only sm danger" onClick={() => confirmDelete(item)} title="Supprimer">
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-bar">
                <div className="info">
                  <strong>{filtered.length}</strong> paramètre(s) — Page <strong>{currentPage}</strong> / {totalPages}
                </div>
                <div className="pagination-controls">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} title="Première">
                    <FontAwesomeIcon icon={faAnglesLeft} />
                  </button>
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage-1))} disabled={currentPage === 1} title="Précédente">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  {[...Array(totalPages)].map((_, idx) => {
                    const p = idx + 1;
                    if (p === 1 || p === totalPages || (p >= currentPage-1 && p <= currentPage+1)) {
                      return (
                        <button key={p} className={p === currentPage ? 'active' : ''} onClick={() => setCurrentPage(p)}>
                          {p}
                        </button>
                      );
                    }
                    if (p === currentPage-2 || p === currentPage+2) return <span key={p} className="ellipsis">…</span>;
                    return null;
                  })}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage+1))} disabled={currentPage === totalPages} title="Suivante">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} title="Dernière">
                    <FontAwesomeIcon icon={faAnglesRight} />
                  </button>
                  <div className="per-page">
                    <span>Par page</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- MODAL CRÉATION / ÉDITION --- */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static" className="modal-glass">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editing ? faEdit : faPlus} className="text-primary me-2" />
            {editing ? 'Modifier le paramètre' : 'Ajouter un paramètre'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="nom"
                value={form.nom}
                onChange={handleChange}
                placeholder="Ex: SMIC, Taux_AMO"
                isInvalid={!!formErrors.nom}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">{formErrors.nom}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Valeur <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="valeur"
                value={form.valeur}
                onChange={handleChange}
                placeholder="Ex: 2500, 0.15"
                isInvalid={!!formErrors.valeur}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">{formErrors.valeur}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Description optionnelle"
                disabled={submitting}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Année</Form.Label>
              <Form.Control
                type="number"
                name="annee"
                value={form.annee}
                onChange={handleChange}
                placeholder="Ex: 2025"
                disabled={submitting}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button variant="secondary" className="btn-glass outline" onClick={() => setShowModal(false)} disabled={submitting}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" className="btn-glass primary" disabled={submitting}>
                {submitting ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSave} className="me-1" />}
                {editing ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* --- MODAL SUPPRESSION --- */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static" className="modal-glass" size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Supprimer définitivement ce paramètre ?</p>
          {deleteTarget && <p className="text-secondary small"><strong>{deleteTarget.nom}</strong> = {deleteTarget.valeur}</p>}
          <small className="text-muted">Cette action est irréversible.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="btn-glass outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button variant="danger" className="btn-glass danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner as="span" animation="border" size="sm" /> : 'Supprimer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --- TOAST --- */}
      {toast && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.msg}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageParametresPaie;